import React, { useState, useCallback } from 'react';
import styles from './HelpButton.module.css';
import { 
  IconButton, 
  Menu, 
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Zoom,
  Fade,
  Divider
} from '@mui/material';
import { 
  IoHelpOutline,
  IoInformationCircleOutline,
  IoKeypadOutline,
  IoClose
} from 'react-icons/io5';

const HelpButton = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  }, []);
  
  const handleClose = useCallback(() => {
    setIsMenuOpen(false);
    setAnchorEl(null);
  }, []);
  
  const handleMenuItemClick = useCallback((type) => {
    handleClose();
    setDialogType(type);
    setIsDialogOpen(true);
  }, [handleClose]);
  
  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false);
    // Only reset dialogType after transition finishes
    setTimeout(() => {
      setDialogType(null);
    }, 300); // Match with transitionDuration
  }, []);
  
  const shortcuts = [
    { keys: ['/'], action: 'Focus chat input' },
    { keys: ['Enter'], action: 'Send message' },
    { keys: ['Ctrl', 'Space'], action: 'Voice input' },
    { keys: ['Ctrl', 'L'], action: 'Open Canvas List' },
    { keys: ['Ctrl', ','], action: 'Open Settings' },
    { keys: ['↑', '↓'], action: 'Navigate canvases' },
    { keys: ['Esc'], action: 'Close panels' },
    { keys: ['Ctrl', 'H'], action: 'Open Messages' },
  ];

  return (
    <div className={styles.helpButtonContainer}>
      <Tooltip 
        title="Help & Resources" 
        placement="left" 
        arrow 
        TransitionComponent={Zoom}
        enterDelay={400}
      >
        <button 
          className={styles.helpButton}
          onClick={handleClick}
          aria-label="Help and resources"
        >
          ?
        </button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        className={styles.menu}
        classes={{ paper: styles.menuPaper }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        TransitionComponent={Fade}
        transitionDuration={250}
      >
        <MenuItem 
          className={styles.menuItem}
          onClick={() => handleMenuItemClick('shortcuts')}
        >
          <IoKeypadOutline className={styles.icon} />
          <span className={styles.menuItemText}>Keyboard Shortcuts</span>
        </MenuItem>

        <Divider className={styles.menuDivider} />
        
        <MenuItem 
          className={styles.menuItem}
          onClick={() => handleMenuItemClick('about')}
        >
          <IoInformationCircleOutline className={styles.icon} />
          <span className={styles.menuItemText}>About Pralok</span>
        </MenuItem>
      </Menu>

      <Dialog 
        open={isDialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        classes={{ paper: styles.dialogPaper }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {dialogType && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <div className={styles.dialogTitleContent}>
                {dialogType === 'about' && (
                  <>
                    <IoInformationCircleOutline className={styles.dialogTitleIcon} />
                    <span>About Pralok Assistant</span>
                  </>
                )}
                {dialogType === 'shortcuts' && (
                  <>
                    <IoKeypadOutline className={styles.dialogTitleIcon} />
                    <span>Keyboard Shortcuts</span>
                  </>
                )}
                <IconButton 
                  onClick={handleDialogClose} 
                  size="small"
                  className={styles.closeButton}
                  aria-label="Close dialog"
                >
                  <IoClose />
                </IconButton>
              </div>
            </DialogTitle>

            <DialogContent className={styles.dialogContent}>
              {dialogType === 'about' && (
                <div className={styles.aboutContainer}>
                  <div className={styles.logoSection}>
                    <img src="/logo.svg" alt="Pralok Logo" className={styles.logo} />
                  </div>
                  
                  <div className={styles.dialogDescription}>
                    Pralok Assistant is an AI-powered platform that helps you create and explore 
                    interactive learning experiences through dynamic canvases.
                  </div>
                  
                  <div className={styles.infoBox}>
                    <IoInformationCircleOutline className={styles.infoBoxIcon} />
                    <span>Please verify all AI-generated responses for accuracy.</span>
                  </div>
                  
                  <div className={styles.versionInfo}>
                    <div>Version</div>
                    <div>1.0.0</div>
                  </div>
                  
                  <div className={styles.versionInfo}>
                    <div>Created by</div>
                    <div>Pralok Team</div>
                  </div>
                </div>
              )}

              {dialogType === 'shortcuts' && (
                <div className={styles.shortcutContainer}>
                  <div className={styles.shortcutList}>
                    {shortcuts.map((shortcut, index) => (
                      <div key={index} className={styles.shortcutItem}>
                        <div className={styles.shortcutKeys}>
                          {shortcut.keys.map((key, keyIndex) => (
                            <React.Fragment key={keyIndex}>
                              <span className={styles.keyBox}>{key}</span>
                              {keyIndex < shortcut.keys.length - 1 && key !== '↑' && (
                                <span className={styles.keyPlus}>+</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                        <div className={styles.shortcutAction}>
                          {shortcut.action}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default HelpButton;
